import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, AlertTriangle, FileText, ArrowLeft, Download, LogIn, Clock, CircleDollarSign } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBs = (n) =>
  `Bs. ${(Number(n) || 0).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MESES_ES = ["", "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const labelPeriodo = (p) => {
  if (!p || !/^\d{4}-\d{2}$/.test(p)) return p || "-";
  const [y, m] = p.split("-");
  return `${MESES_ES[Number(m)] || m} ${y}`;
};

const badgeEstatusPago = (estatus_pago, estatus) => {
  const norm = String(estatus_pago || estatus || "").toLowerCase();
  if (norm.includes("pag") && !norm.includes("sin") && !norm.includes("parcial")) {
    return { label: "PAGADA", color: "bg-emerald-500", Icon: CheckCircle2, ring: "ring-emerald-300/50" };
  }
  if (norm.includes("parcial")) {
    return { label: "PARCIALMENTE PAGADA", color: "bg-amber-500", Icon: Clock, ring: "ring-amber-300/50" };
  }
  if (norm.includes("anul")) {
    return { label: "ANULADA", color: "bg-red-500", Icon: XCircle, ring: "ring-red-300/50" };
  }
  return { label: "PENDIENTE DE PAGO", color: "bg-orange-500", Icon: AlertTriangle, ring: "ring-orange-300/50" };
};

export default function PagarPlanillaPage() {
  const params = useParams();
  const nro = params["*"] || "";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await axios.get(`${API}/public/planilla/verificar/${encodeURIComponent(nro)}`);
        setData(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "No se pudo verificar la planilla.");
      } finally { setLoading(false); }
    };
    if (nro) run(); else { setLoading(false); setError("Falta el número de planilla."); }
  }, [nro]);

  const descargar = () => {
    if (!data?.id || !data?.share_token) return;
    const url = `${API}/public/planilla/${data.id}/pdf?token=${data.share_token}`;
    const a = document.createElement("a");
    a.href = url; a.target = "_blank"; a.rel = "noopener";
    a.click();
  };

  const encontrada = !!data?.encontrada;
  const badge = encontrada ? badgeEstatusPago(data?.estatus_pago, data?.estatus) : null;

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: "linear-gradient(160deg,#052e16 0%,#14532D 45%,#166534 100%)" }}
      data-testid="pagar-planilla-page"
    >
      {/* Patrón sutil de fondo */}
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle at 25px 25px, #FFF 2px, transparent 0)",
          backgroundSize: "50px 50px",
        }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-8">
        {/* Header con branding */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-white">
            <div className="text-xs uppercase tracking-widest opacity-80">Palma Plus · Saneamiento Urbano</div>
            <h1 className="font-display font-bold text-2xl">Estatus de planilla · SISAUR</h1>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20">
              <ArrowLeft className="w-4 h-4 mr-1" /> Volver
            </Button>
          </Link>
        </div>

        {/* Contenido principal */}
        <Card className="p-6 bg-white shadow-2xl border-0 rounded-2xl">
          {loading ? (
            <div className="py-12 text-center text-slate-600" data-testid="pagar-loading">
              <div className="animate-pulse">Verificando planilla…</div>
            </div>
          ) : error ? (
            <div className="py-8 text-center" data-testid="pagar-error">
              <XCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
              <div className="text-slate-800 font-semibold mb-2">Error al verificar</div>
              <div className="text-sm text-slate-600">{error}</div>
            </div>
          ) : !encontrada ? (
            <div className="py-8 text-center" data-testid="pagar-no-encontrada">
              <XCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
              <div className="text-slate-900 font-bold text-lg mb-2">Planilla no encontrada</div>
              <div className="text-sm text-slate-600 mb-4">{data?.mensaje || "No existe una planilla con este número."}</div>
              <div className="text-xs text-slate-500 font-mono bg-slate-50 border border-slate-200 rounded p-2 inline-block">
                Nº consultado: {data?.nro_planilla || nro}
              </div>
            </div>
          ) : (
            <>
              {/* Badge de estatus */}
              {badge && (
                <div className="text-center mb-6" data-testid="pagar-badge">
                  <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full ${badge.color} text-white font-bold shadow-lg ring-4 ${badge.ring}`}>
                    <badge.Icon className="w-5 h-5" />
                    <span className="tracking-wider text-sm">{badge.label}</span>
                  </div>
                </div>
              )}

              {/* Nº planilla destacado */}
              <div className="text-center mb-6">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Nº de planilla</div>
                <div className="font-mono font-bold text-2xl text-slate-900" data-testid="pagar-nro-planilla">
                  {data.nro_planilla}
                </div>
              </div>

              {/* Datos */}
              <div className="space-y-3 mb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Contribuyente</div>
                    <div className="font-semibold text-slate-900 text-sm truncate" title={data.contribuyente_nombre}>
                      {data.contribuyente_nombre}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">RIF / Cédula</div>
                    <div className="font-mono font-semibold text-slate-900 text-sm">{data.contribuyente_rif}</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Período</div>
                    <div className="font-semibold text-slate-900 text-sm">
                      {labelPeriodo(data.periodo || data.mes_anio_declaracion)}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Tipo</div>
                    <div className="font-semibold text-slate-900 text-sm">{data.tipo_declaracion || "—"}</div>
                  </div>
                </div>

                {/* Montos */}
                <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl">
                  <div className="flex items-center gap-2 text-green-800 font-semibold mb-2">
                    <CircleDollarSign className="w-4 h-4" />
                    <span className="text-sm">Montos</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 tracking-wider">Total</div>
                      <div className="font-mono font-bold text-slate-900" data-testid="pagar-monto-total">
                        {fmtBs(data.monto_total_bs)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 tracking-wider">Pagado</div>
                      <div className="font-mono font-semibold text-emerald-700">{fmtBs(data.monto_pagado_bs)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase text-slate-500 tracking-wider">Saldo</div>
                      <div className={`font-mono font-bold ${(data.saldo_pendiente_bs || 0) > 0 ? "text-orange-700" : "text-emerald-700"}`}
                           data-testid="pagar-saldo-pendiente">
                        {fmtBs(data.saldo_pendiente_bs)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Acciones */}
              <div className="flex flex-col sm:flex-row gap-3">
                {data.share_token && data.id && (
                  <Button
                    onClick={descargar}
                    className="flex-1 bg-slate-800 hover:bg-slate-900 text-white"
                    data-testid="btn-descargar-planilla"
                  >
                    <Download className="w-4 h-4 mr-2" /> Descargar planilla (PDF)
                  </Button>
                )}
                {(data.saldo_pendiente_bs || 0) > 0 && (
                  <Link to="/login/contribuyente" className="flex-1">
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-ingresar-portal">
                      <LogIn className="w-4 h-4 mr-2" /> Ingresar al portal para pagar
                    </Button>
                  </Link>
                )}
              </div>

              <div className="mt-4 text-center text-xs text-slate-500">
                <FileText className="w-3 h-3 inline mr-1" />
                Verificación oficial en línea · SISAUR
              </div>
            </>
          )}
        </Card>

        <div className="mt-4 text-center text-xs text-white/70">
          Sistema de Saneamiento Urbano · Palma Plus
        </div>
      </div>
    </div>
  );
}
